import { apiClient } from '../lib/apiClient'
import type { Organization, OrgMember } from '../types'

export interface AuthUser {
  id: string
  email: string
  display_name?: string
  onboarding_completed?: boolean
}

export interface AuthMeResponse {
  user: AuthUser
  organization: Organization
  orgMember: OrgMember
}

export interface SignInResponse {
  token: string
  user: AuthUser
  organization: Organization
  orgMember: OrgMember
}

export interface SignUpResponse {
  token: string
  user: AuthUser
  organization: Organization | null
  orgMember: OrgMember | null
}

export const authApi = {
  async signIn(email: string, password: string): Promise<SignInResponse> {
    const result = await apiClient.post<SignInResponse>('/auth/login', { email, password })
    apiClient.setToken(result.token)
    return result
  },

  async signUp(email: string, password: string, displayName: string, orgName?: string): Promise<SignUpResponse> {
    const result = await apiClient.post<SignUpResponse>('/auth/register', {
      email,
      password,
      displayName,
      ...(orgName ? { orgName } : {}),
    })
    apiClient.setToken(result.token)
    return result
  },

  async signOut(): Promise<void> {
    try {
      await apiClient.post('/auth/logout')
    } finally {
      apiClient.clearToken()
    }
  },

  async getMe(): Promise<AuthMeResponse | null> {
    try {
      return await apiClient.get<AuthMeResponse>('/auth/me')
    } catch (err: any) {
      if (err?.status === 401) return null
      throw err
    }
  },

  async completeOnboarding(): Promise<void> {
    try {
      await apiClient.post('/auth/onboarding/complete')
    } catch (err) {
      console.error('[authApi] Failed to mark onboarding complete:', err)
    }
  },

  getMicrosoftOAuthUrl(): string {
    const base = import.meta.env.VITE_API_BASE_URL || '/api'
    const redirect = `${window.location.origin}/`
    return `${base}/auth/microsoft?redirectTo=${encodeURIComponent(redirect)}`
  },

  getGoogleOAuthUrl(): string {
    const base = import.meta.env.VITE_API_BASE_URL || '/api'
    const redirect = `${window.location.origin}/`
    return `${base}/auth/google?redirectTo=${encodeURIComponent(redirect)}`
  },
}
